import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { EllipsisHorizontalIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { Box, CheckCircle2, CloudUpload, KeyRound, Plus, Trash2, XCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import { Drawer, DrawerClose, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '../../../../empreenderUI/ui/Drawer';
import { Button } from '../../../../empreenderUI/ui/Button';
import Input from '../../../../empreenderUI/ui/Input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../../../empreenderUI/ui/Tooltip/Tooltip';
import { translations } from '../../../../utils/language';

function splitCodes(text) {
  const parts = String(text || '')
    .split(/[\n\r,;]+/)
    .map((c) => c.trim())
    .filter(Boolean);

  // Ignora cabeçalho comum de planilha de exemplo
  if (parts.length && /^(chave|chaves|codigo|código|codigos|códigos|key|keys|serial)$/i.test(parts[0])) {
    return parts.slice(1);
  }
  return parts;
}

function getAuthHeaders() {
  const token = JSON.parse(localStorage.getItem('depoimentos@login'))?.access_token?.token;
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function emptyCodeRow() {
  return { id: null, serial: '', used: false, persistedUsed: false, data_recieved: null };
}

function normalizeUsed(value) {
  return value === true || value === 1 || value === '1';
}

function formatReceivedDateBrasilia(value, lang = 'pt') {
  if (value == null || value === '') return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat(lang === 'es' ? 'es-ES' : 'pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function normalizeCodeRows(rows) {
  return (Array.isArray(rows) ? rows : [])
    .map((row) => {
      const used = normalizeUsed(row?.used);
      return {
        id: row?.id ?? null,
        serial: String(row?.serial_number || row?.serialNumber || '').trim(),
        used,
        // Status já salvo como indisponível não pode voltar a disponível
        persistedUsed: used,
        data_recieved: row?.data_recieved ?? row?.dataReceived ?? null,
      };
    })
    .filter((row) => row.serial || row.id);
}

export default function ModalChavesCodigosNew(props) {
  const dispatch = useDispatch();
  const language = useSelector((state) => state.reducerLanguage);
  const lang = language?.data || 'pt';
  const licenses = useSelector((state) => state.reducerLicenses);
  const reducerControlVariants = useSelector((state) => state.reducerControlVariants);
  const reducerDeleteLicenses = useSelector((state) => state.reducerDeleteLicenses);

  const [view, setView] = useState('list'); // list | form | rename
  const [selectedLicense, setSelectedLicense] = useState(null);
  const [title, setTitle] = useState('');
  const [codes, setCodes] = useState([emptyCodeRow()]);
  const [menuOpenId, setMenuOpenId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadingCodes, setLoadingCodes] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [dragging, setDragging] = useState(false);
  const [initialCodeIds, setInitialCodeIds] = useState([]);
  const fileInputRef = useRef(null);

  const licenseList = licenses?.data?.data || [];
  const nonEmptyCodes = useMemo(() => codes.filter((c) => c.serial.trim()), [codes]);

  const goSearchLicenses = () => {
    dispatch({
      type: 'SAGA_LIST_LICENSES',
      payload: { search: '', perPage: '50', page: 1 },
    });
  };

  const fetchLicenseCodes = async (licenseId) => {
    const headers = getAuthHeaders();
    const res = await fetch(
      `${process.env.REACT_APP_API_URL}/codes/${licenseId}?per_page=200&page=1&search=&used=all`,
      { headers },
    );
    if (!res.ok) throw new Error(translations['keys-err-load-codes'][lang]);
    const json = await res.json();
    const rows = json?.data?.data || json?.data || [];
    return normalizeCodeRows(rows);
  };

  const resetForm = () => {
    setSelectedLicense(null);
    setTitle('');
    setCodes([emptyCodeRow()]);
    setRenameName('');
    setInitialCodeIds([]);
    setLoadingCodes(false);
  };

  const openCreateForm = () => {
    resetForm();
    setView('form');
  };

  const openEditForm = async (license) => {
    setSelectedLicense(license);
    setTitle(license?.name || '');
    setCodes([emptyCodeRow()]);
    setInitialCodeIds([]);
    setMenuOpenId(null);
    setView('form');
    setLoadingCodes(true);
    try {
      const rows = await fetchLicenseCodes(license.id);
      setInitialCodeIds(rows.map((r) => r.id).filter(Boolean));
      setCodes(rows.length ? rows : [emptyCodeRow()]);
    } catch {
      toast.error(translations['keys-err-load-license-codes'][lang]);
      setCodes([emptyCodeRow()]);
    } finally {
      setLoadingCodes(false);
    }
  };

  const openRename = (license) => {
    setSelectedLicense(license);
    setRenameName(license?.name || '');
    setMenuOpenId(null);
    setView('rename');
  };

  const closeModal = () => {
    props.setVisible?.(false);
    dispatch({ type: 'VARIANTS_CONTROL_RESET' });
    setView('list');
    resetForm();
    setMenuOpenId(null);
    props.close?.();
  };

  const handleOpenChange = (open) => {
    if (!open) closeModal();
  };

  useEffect(() => {
    if (!props.visible) return;

    goSearchLicenses();
    setMenuOpenId(null);

    if (props.initialLicense?.id) {
      openEditForm(props.initialLicense);
      return;
    }

    setView('list');
    resetForm();
  }, [props.visible, props.initialLicense?.id]);

  useEffect(() => {
    if (!props.visible) return;
    if (reducerDeleteLicenses.loading) return;
    if (reducerDeleteLicenses.error) {
      toast.error(translations['keys-err-delete-license'][lang]);
      return;
    }
  }, [reducerDeleteLicenses, props.visible]);

  const updateCodeAt = (index, value) => {
    setCodes((prev) => prev.map((c, i) => (i === index ? { ...c, serial: value } : c)));
  };

  const updateCodeStatusAt = (index, used) => {
    setCodes((prev) =>
      prev.map((c, i) => {
        if (i !== index) return c;
        // Só trava se já estava indisponível no servidor
        if (c.persistedUsed && !normalizeUsed(used)) return c;
        return { ...c, used: normalizeUsed(used) };
      }),
    );
  };

  const removeCodeAt = (index) => {
    setCodes((prev) => {
      if (prev[index]?.persistedUsed) return prev;
      if (prev.length <= 1) return [emptyCodeRow()];
      return prev.filter((_, i) => i !== index);
    });
  };

  const addCodeInput = () => {
    setCodes((prev) => [...prev, emptyCodeRow()]);
  };

  const handlePasteCodes = (index, event) => {
    const text = event.clipboardData?.getData('text') || '';
    const parts = splitCodes(text);
    if (parts.length <= 1) return;

    event.preventDefault();
    setCodes((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], serial: parts[0] };
      const rest = parts.slice(1).map((serial) => ({ id: null, serial, used: false, persistedUsed: false, data_recieved: null }));
      next.splice(index + 1, 0, ...rest);
      return next;
    });
  };

  const importCsvFile = async (file) => {
    if (!file) return;

    const name = (file.name || '').toLowerCase();
    const isAllowed = name.endsWith('.csv') || name.endsWith('.txt') || file.type === 'text/csv' || file.type === 'text/plain' || file.type === 'application/vnd.ms-excel';

    if (!isAllowed) {
      toast.error(translations['keys-err-file-type'][lang]);
      return;
    }

    try {
      const text = await file.text();
      const parts = splitCodes(text);
      if (!parts.length) {
        toast.error(translations['keys-err-no-keys-file'][lang]);
        return;
      }
      setCodes((prev) => {
        const kept = prev.filter((c) => c.serial.trim() || c.id);
        const imported = parts.map((serial) => ({
          id: null,
          serial,
          used: false,
          persistedUsed: false,
          data_recieved: null,
        }));
        if (!kept.length && !imported.length) return [emptyCodeRow()];
        return [...kept, ...imported];
      });
      toast.success(`${parts.length} ${translations['keys-imported-suffix'][lang]}`);
    } catch {
      toast.error(translations['keys-err-read-file'][lang]);
    }
  };

  const handleCsvFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    await importCsvFile(file);
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(true);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    await importCsvFile(file);
  };

  const variantAlreadyHasLicense = (variant, licenseId) => {
    const links = variant?.variants?.variantLinkLicenses || variant?.variantLinkLicenses || [];
    return links.some((link) => {
      const linkedId = link?.license_id ?? link?.license?.id;
      const active = link?.license?.active;
      if (active === 0 || active === false) return false;
      return Number(linkedId) === Number(licenseId);
    });
  };

  const linkLicenseToVariants = async (licenseId) => {
    const headers = getAuthHeaders();
    const variants = reducerControlVariants?.data || [];
    const toLink = variants.filter((variant) => !variantAlreadyHasLicense(variant, licenseId));

    if (!toLink.length) return;

    await Promise.all(
      toLink.map((variant) =>
        fetch(`${process.env.REACT_APP_API_URL}/variant_link_license`, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            license_id: licenseId,
            variant_id: variant.variants.id,
          }),
        }).then((res) => {
          if (!res.ok) throw new Error(`${translations['keys-err-link-variant'][lang]} ${variant.variants?.id}`);
          return res.json();
        }),
      ),
    );
  };

  const createLicense = async (name) => {
    const headers = getAuthHeaders();
    const res = await fetch(`${process.env.REACT_APP_API_URL}/licenses`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ name }),
    });
    if (!res.ok) throw new Error(translations['keys-err-create-license'][lang]);
    const json = await res.json();
    return json?.data?.data || json?.data || json;
  };

  const updateLicense = async (licenseId, name) => {
    const headers = getAuthHeaders();
    const res = await fetch(`${process.env.REACT_APP_API_URL}/licenses/${licenseId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ name }),
    });
    if (!res.ok) throw new Error(translations['keys-err-update-license'][lang]);
  };

  const createCode = async (licenseId, serialNumber) => {
    const headers = getAuthHeaders();
    const res = await fetch(`${process.env.REACT_APP_API_URL}/codes`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ licenseId, serialNumber }),
    });
    if (!res.ok) throw new Error(`${translations['keys-err-create-code'][lang]} ${serialNumber}`);
    const json = await res.json();
    return json?.data?.data || json?.data || json;
  };

  const updateCode = async (codeId, serialNumber, used = false) => {
    const headers = getAuthHeaders();
    const res = await fetch(`${process.env.REACT_APP_API_URL}/codes/${codeId}`, {
      method: 'PUT',
      headers,
      body: JSON.stringify({ serial_number: serialNumber, used: Number(used) }),
    });
    if (!res.ok) throw new Error(`${translations['keys-err-update-code'][lang]} ${serialNumber}`);
  };

  const deleteCode = async (codeId) => {
    const headers = getAuthHeaders();
    const res = await fetch(`${process.env.REACT_APP_API_URL}/codes/${codeId}`, {
      method: 'DELETE',
      headers,
    });
    if (!res.ok) throw new Error(translations['keys-err-remove-code'][lang]);
  };

  const syncCodesForLicense = async (licenseId) => {
    const currentIds = new Set(nonEmptyCodes.map((c) => c.id).filter(Boolean));

    // Remove códigos que saíram do formulário
    for (const id of initialCodeIds) {
      if (!currentIds.has(id)) {
        await deleteCode(id);
      }
    }

    for (const row of nonEmptyCodes) {
      const serial = row.serial.trim();
      if (row.id) {
        await updateCode(row.id, serial, row.used);
      } else {
        const created = await createCode(licenseId, serial);
        if (row.used && created?.id) {
          await updateCode(created.id, serial, true);
        }
      }
    }
  };

  const handleSaveAndLink = async () => {
    const name = title.trim();
    if (!name) {
      toast.error(translations['keys-toast-title-required'][lang]);
      return;
    }
    if (nonEmptyCodes.length === 0) {
      toast.error(translations['keys-toast-add-one'][lang]);
      return;
    }

    setSaving(true);
    try {
      let licenseId = selectedLicense?.id;

      if (licenseId) {
        // Sempre atualiza a licença existente (não cria outra)
        if (name !== selectedLicense.name) {
          await updateLicense(licenseId, name);
        }
        await syncCodesForLicense(licenseId);
      } else {
        const created = await createLicense(name);
        licenseId = created?.id;
        if (!licenseId) throw new Error(translations['keys-err-license-no-id'][lang]);
        for (const row of nonEmptyCodes) {
          const serial = row.serial.trim();
          const created = await createCode(licenseId, serial);
          if (row.used && created?.id) {
            await updateCode(created.id, serial, true);
          }
        }
      }

      await linkLicenseToVariants(licenseId);
      toast.success(selectedLicense ? translations['keys-toast-updated-linked'][lang] : translations['keys-toast-linked'][lang]);
      props.goSearch?.();
      closeModal();
    } catch (err) {
      toast.error(err?.message || translations['keys-err-save-link'][lang]);
    } finally {
      setSaving(false);
    }
  };

  const handleRenameSave = () => {
    if (!selectedLicense?.id || !renameName.trim()) return;
    dispatch({
      type: 'SAGA_UPDATE_LICENSES',
      payload: { id: selectedLicense.id, name: renameName.trim() },
    });
    toast.success(translations['keys-toast-renamed'][lang]);
    props.goSearch?.();
    setTimeout(() => {
      goSearchLicenses();
      setView('list');
      resetForm();
    }, 400);
  };

  const handleDelete = (license) => {
    setMenuOpenId(null);
    if (!window.confirm(`${translations['keys-confirm-delete'][lang]} "${license.name}"?`)) return;
    dispatch({ type: 'SAGA_DELETE_LICENSES', payload: license.id });
    toast.success(translations['keys-toast-deleted'][lang]);
    props.goSearch?.();
    setTimeout(() => goSearchLicenses(), 400);
  };

  const subtitle = props.contextLabel || props.eyebrow || '';

  const renderList = () => (
    <div className='flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-4 custom-scroll'>
      {licenses?.loading ? (
        <p className='text-sm text-gray-500'>{translations['keys-loading-licenses'][lang]}</p>
      ) : licenseList.length === 0 ? (
        <div className='rounded-xl border border-dashed border-gray-200 px-4 py-8 text-center'>
          <KeyRound className='mx-auto size-6 text-gray-400' />
          <p className='mt-3 text-sm text-gray-500'>{translations['keys-empty'][lang]}</p>
          <Button type='button' className='mt-4 bg-blue-600 hover:bg-blue-700' onClick={openCreateForm}>
            <Plus className='size-4' />
            {translations['keys-create-license'][lang]}
          </Button>
        </div>
      ) : (
        <div className='flex flex-col gap-3'>
          {licenseList.map((license) => {
            const available = Number(license.not_used_codes ?? license.notUsedCodes ?? 0);
            const used = Number(license.used_codes ?? license.usedCodes ?? 0);
            const total = available + used;
            return (
              <div key={license.id} className='relative flex items-center gap-2'>
                <button type='button' onClick={() => openEditForm(license)} className='flex min-w-0 flex-1 items-center gap-3 rounded-xl border border-gray-200 bg-white px-3 py-3 text-left transition-colors hover:border-blue-400 hover:bg-blue-50/40'>
                  <div className='flex size-10 shrink-0 items-center justify-center rounded-lg bg-violet-50 text-violet-600'>
                    <Box className='size-5' strokeWidth={1.75} />
                  </div>
                  <div className='min-w-0 flex-1'>
                    <p className='truncate text-sm font-semibold text-gray-900'>
                      {license.name}
                      {total > 0 ? ` - ${total} ${total === 1 ? translations['keys-license-one'][lang] : translations['keys-license-many'][lang]}` : ''}
                    </p>
                    <p className='truncate text-xs text-gray-500'>
                      {available} {translations['keys-available'][lang]}
                      {total > 0 ? ` - ${total} ${translations['keys-keys-lower'][lang]}` : ''}
                    </p>
                  </div>
                </button>
                <div className='relative shrink-0'>
                  <button type='button' className='inline-flex size-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700' onClick={() => setMenuOpenId(menuOpenId === license.id ? null : license.id)} aria-label={translations['keys-options'][lang]}>
                    <EllipsisHorizontalIcon className='size-5' />
                  </button>
                  {menuOpenId === license.id && (
                    <div className='absolute right-0 top-9 z-20 min-w-[160px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg'>
                      <button type='button' className='block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50' onClick={() => openRename(license)}>
                        {translations['medias-menu-renomear'][lang]}
                      </button>
                      <button type='button' className='block w-full px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50' onClick={() => openEditForm(license)}>
                        {translations['keys-add-codes'][lang]}
                      </button>
                      <button type='button' className='block w-full px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50' onClick={() => handleDelete(license)}>
                        {translations['medias-excluir'][lang]}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
          <div className='flex'>
            <button type='button' onClick={openCreateForm} className='w-full inline-flex size-10 items-center justify-center rounded-full border border-dashed border-gray-300 text-gray-500 transition-colors hover:border-blue-400 hover:text-blue-600' aria-label={translations['keys-new-license'][lang]}>
              <PlusIcon className='size-5' />
              {translations['keys-create-license'][lang]}
            </button>
          </div>
        </div>
      )}
    </div>
  );

  const renderForm = () => (
    <>
      <div className='flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-4 custom-scroll'>
        <div className='flex flex-col gap-1.5'>
          <label className='text-xs font-medium text-gray-500'>{translations['keys-title-label'][lang]}</label>
          <Input type='text' value={title} onChange={(e) => setTitle(e.target.value)} placeholder={translations['keys-title-placeholder'][lang]} />
        </div>

        <div className='flex flex-col gap-2'>
          <label className='text-xs font-medium text-gray-500'>{translations['keys-keys'][lang]}</label>
          {loadingCodes ? (
            <p className='text-sm text-gray-500'>{translations['keys-loading-codes'][lang]}</p>
          ) : (
            <>
              <TooltipProvider delayDuration={200}>
                {codes.map((code, index) => {
                  const isUsed = !!code.used;
                  const statusLocked = !!code.persistedUsed;
                  const canRemove = !isUsed && codes.length > 1;
                  const receivedLabel = formatReceivedDateBrasilia(code.data_recieved, lang);
                  const lockedTooltip = receivedLabel ? `${translations['keys-already-used'][lang]} ${receivedLabel}` : translations['keys-already-used'][lang];
                  return (
                    <div key={code.id || `new-${index}`} className='flex items-center gap-2'>
                      <div className='relative min-w-0 flex-1'>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <button
                              type='button'
                              disabled={statusLocked}
                              onClick={() => {
                                if (statusLocked) return;
                                updateCodeStatusAt(index, !isUsed);
                              }}
                              className={`absolute left-2 top-1/2 z-10 inline-flex size-6 -translate-y-1/2 items-center justify-center rounded-md transition-colors ${
                                statusLocked
                                  ? 'text-red-500 cursor-not-allowed'
                                  : isUsed
                                    ? 'text-red-500 hover:bg-red-50'
                                    : 'text-green-600 hover:bg-green-50'
                              }`}
                              aria-label={statusLocked ? lockedTooltip : isUsed ? translations['keys-mark-available'][lang] : translations['keys-mark-unavailable'][lang]}
                            >
                              {isUsed ? <XCircle className='size-4' /> : <CheckCircle2 className='size-4' />}
                            </button>
                          </TooltipTrigger>
                          <TooltipContent side='top'>
                            {statusLocked
                              ? lockedTooltip
                              : isUsed
                                ? translations['keys-tooltip-unavailable'][lang]
                                : translations['keys-tooltip-available'][lang]}
                          </TooltipContent>
                        </Tooltip>
                        <Input
                          type='text'
                          value={code.serial}
                          onChange={(e) => {
                            if (statusLocked) return;
                            updateCodeAt(index, e.target.value);
                          }}
                          onPaste={(e) => {
                            if (statusLocked) {
                              e.preventDefault();
                              return;
                            }
                            handlePasteCodes(index, e);
                          }}
                          readOnly={statusLocked}
                          placeholder={translations['keys-code-placeholder'][lang]}
                          className={`!pl-9 ${
                            isUsed
                              ? statusLocked
                                ? '!border-red-400 bg-red-50/40 text-gray-500 cursor-not-allowed'
                                : '!border-red-400 bg-red-50/40'
                              : '!border-green-500 focus:!border-green-600'
                          }`}
                        />
                      </div>
                      {canRemove ? (
                        <button type='button' onClick={() => removeCodeAt(index)} className='inline-flex size-9 shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700' aria-label={translations['keys-remove-code'][lang]}>
                          <Trash2 className='size-4' />
                        </button>
                      ) : (
                        <span className='inline-flex size-9 shrink-0' aria-hidden />
                      )}
                    </div>
                  );
                })}
              </TooltipProvider>
              <button type='button' onClick={addCodeInput} className='self-start text-sm font-medium text-blue-600 hover:text-blue-700'>
                {translations['keys-add-more'][lang]}
              </button>
            </>
          )}
        </div>

        <div className='flex flex-col gap-1.5'>
          <div className='flex items-center justify-between gap-2'>
            <label className='text-xs font-medium text-gray-500'>{translations['keys-import-csv'][lang]}</label>
            <a href={`${process.env.PUBLIC_URL || ''}/exemplos/exemplo-chaves.csv`} download='exemplo-chaves.csv' className='text-xs font-medium text-blue-600 hover:text-blue-700 whitespace-nowrap'>
              {translations['keys-download-example'][lang]}
            </a>
          </div>
          <button
            type='button'
            onClick={() => fileInputRef.current?.click()}
            onDragEnter={handleDragEnter}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            className={`flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors ${dragging ? 'border-blue-500 bg-blue-50/50' : 'border-gray-200 bg-gray-50 hover:border-blue-400 hover:bg-blue-50/40'}`}
          >
            <CloudUpload className='size-8 text-gray-400' strokeWidth={1.5} />
            <span className='text-sm font-medium text-gray-700'>{translations['keys-dropzone'][lang]}</span>
            <span className='text-xs text-gray-500'>{translations['keys-dropzone-hint'][lang]}</span>
            <input ref={fileInputRef} type='file' accept='.csv,.txt,text/csv,text/plain' onChange={handleCsvFile} className='hidden' />
          </button>
        </div>

        <p className='text-sm text-gray-500'>{nonEmptyCodes.length === 0 ? translations['keys-none-added'][lang] : `${nonEmptyCodes.length} ${translations['keys-added-suffix'][lang]}`}</p>
      </div>

      <div className='shrink-0 border-t border-gray-100 px-6 py-4'>
        <div className='flex items-center justify-between gap-3'>
          <Button
            type='button'
            variant='outline'
            onClick={() => {
              setView('list');
              resetForm();
            }}
          >
            {translations['common-back'][lang]}
          </Button>
          <Button type='button' className='bg-blue-600 hover:bg-blue-700' disabled={saving} onClick={handleSaveAndLink}>
            {saving ? translations['keys-saving'][lang] : translations['keys-save-link'][lang]}
          </Button>
        </div>
      </div>
    </>
  );

  const renderRename = () => (
    <>
      <div className='flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-6 py-4 custom-scroll'>
        <div className='flex flex-col gap-1.5'>
          <label className='text-xs font-medium text-gray-500'>{translations['keys-license-name'][lang]}</label>
          <Input type='text' value={renameName} onChange={(e) => setRenameName(e.target.value)} placeholder={translations['Nome'][lang]} />
        </div>
      </div>
      <div className='shrink-0 border-t border-gray-100 px-6 py-4'>
        <div className='flex items-center justify-between gap-3'>
          <Button
            type='button'
            variant='outline'
            onClick={() => {
              setView('list');
              resetForm();
            }}
          >
            {translations['common-back'][lang]}
          </Button>
          <Button type='button' className='bg-blue-600 hover:bg-blue-700' onClick={handleRenameSave}>
            {translations['Salvar'][lang]}
          </Button>
        </div>
      </div>
    </>
  );

  const headerTitle = view === 'list' ? translations['keys-header-list'][lang] : view === 'rename' ? translations['keys-header-rename'][lang] : translations['keys-header-add'][lang];

  return (
    <Drawer open={!!props.visible} onOpenChange={handleOpenChange} direction='right'>
      <DrawerContent className='!w-[min(420px,100vw)] !max-w-[min(420px,100vw)] !h-full !max-h-[100dvh] border-l border-gray-200 p-0 flex flex-col overflow-hidden bg-white'>
        <DrawerHeader className='relative shrink-0 space-y-0 bg-white px-6 pt-6 pb-2'>
          <div className='flex items-start justify-between gap-3 pr-10'>
            <div className='min-w-0 space-y-1'>
              {view === 'form' && <p className='text-[11px] font-semibold uppercase tracking-wide text-gray-400'>{translations['keys-delivery-details'][lang]}</p>}
              <DrawerTitle className='text-xl font-bold tracking-tight text-gray-900'>{headerTitle}</DrawerTitle>
              {subtitle ? <DrawerDescription className='text-sm font-medium text-gray-500'>{subtitle}</DrawerDescription> : null}
            </div>
          </div>
          <DrawerClose asChild>
            <button type='button' className='absolute right-4 top-4 inline-flex size-8 items-center justify-center rounded-lg bg-gray-100 text-gray-700 transition-colors hover:bg-gray-200' aria-label={translations['common-close'][lang]}>
              <XMarkIcon className='size-5' />
            </button>
          </DrawerClose>
        </DrawerHeader>

        {view === 'list' && renderList()}
        {view === 'form' && renderForm()}
        {view === 'rename' && renderRename()}
      </DrawerContent>
    </Drawer>
  );
}
